<template>
  <div class="space-y-1 w-full">
    <label :id="question.id" class="block text-sm font-semibold leading-6 text-gray-800">
      {{ question.title }}
    </label>
    <template v-if="question.qstList?.qstListRows && question.qstList?.qstListRows.length <= 5">
      <div class="flex flex-wrap gap-3 justify-start items-center w-full">
        <div class="" v-for="item in question.qstList?.qstListRows" :key="item.id">
          <input
            v-model="data"
            :id="disabled ? 'radio-disabled-' + item.id : 'radio-' + item.id"
            type="radio"
            :value="item.id"
            :name="disabled ? 'radio-disabled-' + item.id : 'radio-' + item.id"
            :disabled="disabled"
            class="hidden peer" />
          <label
            :for="disabled ? 'radio-disabled-' + item.id : 'radio-' + item.id"
            class="flex justify-center items-center py-3 text-sm font-medium text-gray-600 rounded-md border border-gray-300 min-w-24 peer-checked:bg-theme-700 peer-checked:text-white"
            >{{ item.description }}
          </label>
        </div>
      </div>
    </template>
    <template v-else>
      <SingleSelect
        v-if="question.qstList?.qstListRows"
        :options="question.qstList?.qstListRows"
        item-text="description"
        v-model="data"
        :disabled="disabled"
        @update:model-value="handleSelectionChange">
        <template #selected-option>{{ getDescription }}</template>
      </SingleSelect>
    </template>
    <p v-if="v$.model.$error && v$.model.$dirty" class="mt-1 text-xs text-red-600">
      {{ firstError }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import SingleSelect from '../common/SingleSelect.vue';
import type { QstListRow, QstQuestionDetails } from '@/types/Questionnaires';
import { useI18n } from 'vue-i18n';
import { helpers, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';

const props = withDefaults(
  defineProps<{
    question: QstQuestionDetails;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  }
);

const data = defineModel<string>();

const getDescription = computed(() => {
  if (!data.value) return 'No selection';
  let item;
  if (props.question.qstList?.qstListRows) {
    item = props.question.qstList?.qstListRows.find((item) => item.id === data.value);
  }
  return item ? item.description : '';
});

function handleSelectionChange(newValue: QstListRow) {
  data.value = newValue.id;
}

const { t: $t } = useI18n({ useScope: 'global' });
const rules = computed(() => {
  const record: Record<string, any> = {};

  if (!props.question.allowNull) {
    record.required = helpers.withMessage($t('messages.errors.requiredField'), required);
  }

  return { model: record };
});

const v$ = useVuelidate(rules, { model: data }, { $lazy: true });

const firstError = computed(() => {
  const errs = v$.value.model.$errors;
  return errs.length ? errs[0].$message : $t('messages.errors.invalidValue');
});
</script>
